/**
 * Design system component: PossessionChain
 *
 * Visual block that shows the current custody chain for an item — always
 * rendered, even when the item is available (owner-only node). This ensures
 * borrowers can always see who owns the item without having to interact.
 *
 * Layout:
 *   [Owner avatar] Owner name · "Has item" badge   (when available)
 *      |  (dashed connector line)
 *   [Borrower avatar] Borrower name · Due/Overdue chip  (when on loan)
 *
 * The owner avatar uses the category background colour; the borrower avatar
 * uses the accent or warning colour when the current user is the borrower.
 * Overdue items display the due chip in warning colours.
 */
import { memo } from 'react';
import { T, CAT_FALLBACK, RADIUS, SHADOW } from '@/lib/tokens';
import { Avatar } from './Avatar';

interface PossessionChainProps {
  ownerName: string;
  ownerUserId: string;
  borrowerName?: string | null;
  borrowerUserId?: string | null;
  dueAt?: string | null;
  category?: string | null;
  currentUserId: string;
  /** When true, borrower identity is hidden (cross-group loan). */
  masked?: boolean;
}

export const PossessionChain = memo(function PossessionChain({
  ownerName,
  ownerUserId,
  borrowerName,
  borrowerUserId,
  dueAt,
  category,
  currentUserId,
  masked = false,
}: PossessionChainProps) {
  const catColours = category && T.cat[category] ? T.cat[category] : CAT_FALLBACK;
  const isOnLoan = !!borrowerName;
  const borrowerIsCurrentUser = borrowerUserId === currentUserId;

  const formatDue = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const isOverdue = dueAt ? new Date(dueAt) < new Date() : false;

  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.lg,
        overflow: 'hidden',
      }}
    >
      {/* Header strip */}
      <div
        style={{
          padding: '9px 14px',
          background: T.surfaceSubtle,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: T.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            fontWeight: 700,
          }}
        >
          Possession chain
        </span>
      </div>

      {/* Chain body */}
      <div style={{ padding: 14 }}>
        {/* Owner row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            name={ownerName}
            size={34}
            bg={catColours.bg}
            isCurrentUser={ownerUserId === currentUserId}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{ownerName}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>Owner</div>
          </div>
          {!isOnLoan && (
            <span
              style={{
                fontSize: 11,
                color: T.success,
                fontWeight: 600,
                background: T.successSoft,
                border: `1px solid ${T.successBorder}`,
                padding: '3px 8px',
                borderRadius: 6,
              }}
            >
              Has item
            </span>
          )}
        </div>

        {/* Borrower row (if on loan) */}
        {isOnLoan && (
          <>
            {/* Dashed connector */}
            <div
              style={{
                marginLeft: 17,
                borderLeft: `2px dashed ${T.borderStrong}`,
                height: 14,
                marginTop: 3,
                marginBottom: 3,
              }}
            />

            {/* Borrower */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {masked ? (
                <>
                  <Avatar name="?" size={34} bg={T.surfaceSubtle} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.textMuted, fontWeight: 600 }}>On loan</div>
                    <div style={{ fontSize: 11, color: T.textMuted }}>Another group</div>
                  </div>
                </>
              ) : (
                <>
                  <Avatar
                    name={borrowerName!}
                    size={34}
                    bg={borrowerIsCurrentUser ? T.warningSoft : T.surfaceSubtle}
                    isCurrentUser={borrowerIsCurrentUser}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                      {borrowerName}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted }}>Current holder</div>
                  </div>
                  {dueAt && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 6,
                        background:
                          isOverdue || borrowerIsCurrentUser ? T.warningSoft : T.surfaceSubtle,
                        color: isOverdue || borrowerIsCurrentUser ? T.warning : T.textMuted,
                      }}
                    >
                      {isOverdue ? 'Overdue' : `Due ${formatDue(dueAt)}`}
                    </span>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});
